// @erp/module-lib/fe · scan/link — the DESK's scan-link store (SCAN-4).
//
// GATE (c) LIVES HERE. modules/lib/decisions.md records it as blocking:
//
//   "The desk Issue/Return flow must NEVER auto-commit a circulation action on a
//    received scan. A scan is untrusted operator input over an unauthenticated
//    public surface (no server-side dedup/replay guard by design); the desk must
//    stage a scan for explicit staff confirmation, never fire an issue/return
//    off the wire."
//
// So this store deliberately exposes `staged` — the last scan awaiting a human —
// plus `onStagedForConfirmation`, a notification that a screen may use to STAGE
// the value into its own form (prefill a field, add a cart row, run a read-only
// lookup). No API here commits anything; that is the point, and it is why the
// source's `onScan → auto-fill → auto-submit` chain is not ported.
//
// Two lifetimes share one core:
//   • `createScanLink()` — component-scoped: the link dies with the screen.
//     (BulkAddBooks-style embedded panels.)
//   • `scanHub()` (./hub.ts) — TAB-scoped: pair once, navigate freely, exactly
//     the production connector's UX. The hub calls `openScanLink()` and holds
//     the dispose itself.
// Both lifetimes share ONE browser-scoped session: `openScanLink` reuses the
// persisted id when this browser holds one, else mints with `makeScanSession()`
// — random per pairing act, NEVER user-derived (the id is the sole bearer
// capability; see bridge.ts). Persistence changes only how LONG a pairing
// lives; it never changes how the id is minted.
import { createSignal, onCleanup } from "solid-js";
import type { Accessor } from "solid-js";
import { makeScanCode, makeScanSession } from "../../schemas/scan.js";
import type { DeviceInfo } from "../../schemas/scan.js";
import { phoneScanUrl, registerHost, subscribeHost, unpairDevice } from "./bridge.js";
import type { ScanEvent } from "./bridge.js";

/** Rolling feed cap — enough for a desk shift's eyeball history, tiny in memory. */
const RECENT_CAP = 30;

/* ────────────────────── browser-scoped session persistence ────────────────────── */
// Pair a phone ONCE and the whole browser listens: the session id survives
// reloads and new tabs (decisions.md, browser-scoped pairing lifetime). Only
// the id is stored — desk sockets stay cookie-authenticated and receive-only,
// and gate (c) staging is untouched. Every storage access is try/catch'd: a
// private window simply degrades to the old tab-scoped lifetime.

/** The localStorage key holding the desk's pairing session id. */
export const SCAN_SESSION_STORE_KEY = "erp.lib.scan.session";

/** The persisted pairing session id, or null (nothing stored / storage unavailable). */
export const loadStoredScanSession = (): string | null => {
  try {
    const v = localStorage.getItem(SCAN_SESSION_STORE_KEY);
    return v !== null && v.length > 0 ? v : null;
  } catch {
    return null;
  }
};

const storeScanSession = (session: string): void => {
  try {
    localStorage.setItem(SCAN_SESSION_STORE_KEY, session);
  } catch {
    /* private mode — the pairing degrades to tab-scoped */
  }
};

/** Forget the persisted pairing — the next link mints a fresh session. */
export const clearStoredScanSession = (): void => {
  try {
    localStorage.removeItem(SCAN_SESSION_STORE_KEY);
  } catch {
    /* ignore */
  }
};

export interface ScanLink {
  /** The pairing session id — minted once per link, the DO instance name. */
  session: Accessor<string>;
  /** The URL to render as the pairing QR. */
  pairingUrl: Accessor<string>;
  /** The 6-digit code the operator reads out to the phone. */
  code: Accessor<string>;
  /** Phones currently paired to this session. */
  devices: Accessor<DeviceInfo[]>;
  /** Is the desk's relay socket up? */
  connected: Accessor<boolean>;
  /**
   * The scan awaiting explicit confirmation — gate (c). Null when nothing is
   * pending. A screen RENDERS this; it must not act on it by itself.
   */
  staged: Accessor<ScanEvent | null>;
  /** Dismiss the staged scan (the operator acted on it, or rejected it). */
  clearStaged: () => void;
  /** Newest-first rolling feed of received scans (display only). */
  recent: Accessor<ScanEvent[]>;
  clearRecent: () => void;
  /** Mint + register a fresh pairing code on demand (operator's regenerate). */
  regenerateCode: () => Promise<void>;
  /** Drop one paired phone. */
  removeDevice: (id: string) => Promise<void>;
  /** Drop EVERY paired phone (the "Disconnect all" desk action). */
  removeAllDevices: () => Promise<void>;
}

export interface ScanLinkOptions {
  /**
   * Called when a scan arrives, AFTER it has been staged — for staging only:
   * prefill a field, add an editable row, run a read-only lookup, nudge focus.
   * It must not commit. Deliberately named to make a misuse read wrong at the
   * call site.
   */
  onStagedForConfirmation?: (ev: ScanEvent) => void;
}

/**
 * Open a desk scan link with NO owner requirement: mint a session, subscribe to
 * the relay, keep the shared pairing code registered, and stage incoming scans.
 * The caller owns the returned `dispose` (the hub keeps it for the tab's life;
 * `createScanLink` hands it to Solid's cleanup).
 */
export function openScanLink(options: ScanLinkOptions = {}): { link: ScanLink; dispose: () => void } {
  // Reuse the browser's persisted session so a reload or a second tab rejoins
  // the phone already paired (the relay broadcasts to EVERY desk socket on the
  // session); mint only when nothing is stored. The minted id is ALWAYS
  // `makeScanSession()` — persistence never touches the minting path.
  const session = loadStoredScanSession() ?? makeScanSession();
  storeScanSession(session);
  const [code, setCode] = createSignal("");
  const [devices, setDevices] = createSignal<DeviceInfo[]>([]);
  const [connected, setConnected] = createSignal(false);
  const [staged, setStaged] = createSignal<ScanEvent | null>(null);
  const [recent, setRecent] = createSignal<ScanEvent[]>([]);

  /** Mint a fresh code and register it with the relay, which fans it to every desk. */
  const regenerate = async (): Promise<void> => {
    const next = makeScanCode();
    setCode(next);
    await registerHost(session, next);
  };

  const dispose = subscribeHost(session, {
    onConnected: setConnected,
    onDevices: (list, needsCode, sharedCode) => {
      setDevices(list);
      // The relay is the code's owner: mint one only when it says it has none
      // (first connect, or a code just consumed by a pairing), otherwise adopt
      // what it broadcasts so every desk on this session shows the same digits.
      if (needsCode) void regenerate();
      else if (sharedCode) setCode(sharedCode);
    },
    onScan: (ev) => {
      setStaged(ev);
      setRecent((prev) => [ev, ...prev].slice(0, RECENT_CAP));
      options.onStagedForConfirmation?.(ev);
    },
  });

  const link: ScanLink = {
    session: () => session,
    pairingUrl: () => phoneScanUrl(session),
    code,
    devices,
    connected,
    staged,
    clearStaged: () => setStaged(null),
    recent,
    clearRecent: () => setRecent([]),
    regenerateCode: regenerate,
    removeDevice: async (id: string) => {
      await unpairDevice(session, id);
    },
    removeAllDevices: async () => {
      // The relay has no bulk op — iterate the roster; each unpair rebroadcasts.
      await Promise.all(devices().map((d) => unpairDevice(session, d.id)));
      // Unpair-all is the operator ending the pairing: forget the persisted
      // session too, so a reload does not resurrect a deliberately-wiped link.
      clearStoredScanSession();
    },
  };

  return { link, dispose };
}

/**
 * Component-scoped scan link: the socket is torn down with the owning screen.
 * Must be called inside a Solid owner (a component body).
 */
export function createScanLink(options: ScanLinkOptions = {}): ScanLink {
  const { link, dispose } = openScanLink(options);
  onCleanup(dispose);
  return link;
}
